import { getRandomColor } from 'module/helpers/get-random-color';
import { useTranslate } from 'module/hooks/use-translation';
import { Option as SelectOption, Select } from '@rocketlane/ui';
import { useNativePermissionSetDefinitionsLightQuery } from 'queries/permission';
import { useGetAllUserGroupsQuery } from 'queries/user-group';
import { FC, useCallback, useMemo } from 'react';
import { GroupBase } from 'react-select';

interface PermissionSelectOption extends SelectOption {
  value: number;
  type?: 'permission' | 'user_group';
}

interface Props {
  value?: {
    userGroupIds: number[];
    permissionSetIds: number[];
  };
  onChange?: (value?: {
    userGroupIds: number[];
    permissionSetIds: number[];
  }) => void;
}

export const PermissionsSelect: FC<Props> = ({ value, onChange }) => {
  const { data: permissionSetDefinitionsLight } =
    useNativePermissionSetDefinitionsLightQuery();
  const { data: userGroups } = useGetAllUserGroupsQuery();
  const translate = useTranslate();
  const anyoneOption = useMemo<PermissionSelectOption>(
    () => ({
      label: translate(
        'knowledge_base.folder.create.permissions.groups.anyone',
      ),
      value: -1,
      color: 'gray',
    }),
    [translate],
  );
  const options = useMemo<GroupBase<PermissionSelectOption>[]>(() => {
    return [
      {
        label: translate(
          'knowledge_base.folder.create.permissions.groups.permission',
        ),
        options:
          permissionSetDefinitionsLight
            ?.filter(
              (permission) =>
                permission.permissionSetObjectType === 'ACCOUNT' &&
                permission.permissionSetSpecificTo === 'NATIVE',
            )
            ?.map(
              (permission): PermissionSelectOption => ({
                label: permission.permissionSetName,
                value: permission.permissionSetId,
                type: 'permission',
                color: getRandomColor(permission.permissionSetId),
              }),
            ) ?? [],
      },
      {
        label: translate(
          'knowledge_base.folder.create.permissions.groups.user_group',
        ),
        options:
          userGroups?.map(
            (userGroup): PermissionSelectOption => ({
              label: userGroup.groupName,
              value: userGroup.groupId,
              type: 'user_group',
              color: getRandomColor(userGroup.groupId),
            }),
          ) ?? [],
      },
    ];
  }, [permissionSetDefinitionsLight, userGroups, translate]);
  const selectedOptions = useMemo(() => {
    if (!value) {
      return [anyoneOption];
    }
    const permissionOptions = options[0].options;
    const groupOptions = options[1].options;
    const selectedGroupOptions =
      groupOptions?.filter((option) =>
        value?.userGroupIds.includes(option.value),
      ) ?? [];
    const selectedPermissionOptions =
      permissionOptions?.filter((option) =>
        value?.permissionSetIds.includes(option.value),
      ) ?? [];

    return [...selectedGroupOptions, ...selectedPermissionOptions];
  }, [anyoneOption, options, value]);
  const handleChange = useCallback(
    (values: readonly PermissionSelectOption[]) => {
      const anyoneOption =
        values.find((option) => option.value === -1) && values.length === 1;

      if (anyoneOption || values.length === 0) {
        onChange?.(undefined);

        return;
      }
      onChange?.({
        userGroupIds: values
          .filter((option) => option.type === 'user_group')
          .map((option) => option.value),
        permissionSetIds: values
          .filter((option) => option.type === 'permission')
          .map((option) => option.value),
      });
    },
    [onChange],
  );

  return (
    <Select<PermissionSelectOption, true>
      options={options}
      isMulti
      hideSelectedOptions={false}
      menuPortalTarget={null}
      value={selectedOptions}
      onChange={handleChange}
    />
  );
};
